import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'card',
    'conversationInput',
    'deleteButton',
    'messageArea',
    'phoneInput',
    'waButton',
    'templateButton',
    'templateMessage',
    'testStatusButton',
    'crmUrlInput',
    'analyzeButton',
    'analysisResults',
    'analysisLoading',
    'analysisContent',
    'editPromptButton',
    'promptEditor',
    'promptTextarea',
    'validateUrlsButton',
    'urlValidationResults',
    'urlValidationLoading',
    'urlValidationContent',
    'validateCrawlerButton',
    'crawlerValidationResults',
    'crawlerValidationLoading',
    'crawlerValidationContent',
    'validateMediaButton',
    'mediaValidationResults',
    'mediaValidationLoading',
    'mediaValidationContent',
    'leadTemplate',
    'leadName',
    'leadPhone',
    'leadEmail',
    'leadBudget',
    'leadBedrooms',
    'leadTransactionType',
    'leadMessage',
    'leadListing',
    'leadOrigin',
    'customOriginFields',
    'customOriginDomain',
    'createLeadButton',
    'leadTestResults',
    'leadTestLoading',
    'leadTestContent',
  ]
  static values = { realEstateAdminId: Number }

  connect() {
    console.log('Platform testing controller connected')
    console.log('Real estate admin ID:', this.realEstateAdminIdValue)
    console.log('Test status buttons found:', this.testStatusButtonTargets.length)
    console.log('All targets:', {
      cards: this.cardTargets.length,
      conversationInputs: this.conversationInputTargets.length,
      deleteButtons: this.deleteButtonTargets.length,
      messageAreas: this.messageAreaTargets.length,
      phoneInputs: this.phoneInputTargets.length,
      waButtons: this.waButtonTargets.length,
      templateButtons: this.templateButtonTargets.length,
      templateMessages: this.templateMessageTargets.length,
      testStatusButtons: this.testStatusButtonTargets.length,
    })

    // Set up origin selection event listener
    if (this.hasLeadOriginTarget) {
      this.leadOriginTarget.addEventListener('change', () => this.handleOriginChange())
    }
  }

  handleOriginChange() {
    const origin = this.leadOriginTarget.value
    const customFields = this.customOriginFieldsTarget

    if (origin === 'custom') {
      customFields.classList.remove('hidden')
    } else {
      customFields.classList.add('hidden')
      if (this.hasCustomOriginDomainTarget) {
        this.customOriginDomainTarget.value = ''
      }
    }
  }

  updateStatus(event) {
    const button = event.currentTarget
    const status = button.dataset.status
    const card = button.closest('[data-platform-key]')
    const platformKey = card.dataset.platformKey

    this.sendStatusUpdate(platformKey, status, card)
  }

  toggleDeleteButton(event) {
    const input = event.currentTarget
    const card = input.closest('[data-platform-key]')
    const deleteButton = card.querySelector("[data-platform-testing-target='deleteButton']")

    if (deleteButton) {
      deleteButton.disabled = input.value.trim() === ''
    }
  }

  deleteConversation(event) {
    const button = event.currentTarget
    const card = button.closest('[data-platform-key]')
    const input = card.querySelector("[data-platform-testing-target='conversationInput']")
    const conversationId = input?.value.trim()

    if (!conversationId) {
      this.showMessage(card, 'ID da conversa é obrigatório', 'error')
      return
    }

    if (!confirm('Tem certeza que deseja deletar esta conversa?')) {
      return
    }

    this.sendDeleteRequest(conversationId, card, input)
  }

  sendStatusUpdate(platformKey, status, card) {
    const url = `/real_estate_admin/${this.realEstateAdminIdValue}/onboarding/update_platform_status`

    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken(),
      },
      body: JSON.stringify({
        platform_key: platformKey,
        status: status,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.showMessage(card, data.message, 'success')
          // Reload page to update UI
          setTimeout(() => window.location.reload(), 1000)
        } else {
          this.showMessage(card, data.error, 'error')
        }
      })
      .catch((error) => {
        this.showMessage(card, 'Erro ao atualizar status', 'error')
        console.error('Error:', error)
      })
  }

  sendDeleteRequest(conversationId, card, input) {
    const url = `/real_estate_admin/${this.realEstateAdminIdValue}/onboarding/delete_conversation`

    fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken(),
      },
      body: JSON.stringify({
        conversation_id: conversationId,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          this.showMessage(card, data.message, 'success')
          input.value = ''
          const deleteButton = card.querySelector("[data-platform-testing-target='deleteButton']")
          if (deleteButton) {
            deleteButton.disabled = true
          }
        } else {
          this.showMessage(card, data.error, 'error')
        }
      })
      .catch((error) => {
        this.showMessage(card, 'Erro ao deletar conversa', 'error')
        console.error('Error:', error)
      })
  }

  showMessage(card, text, type) {
    const messageArea = card.querySelector("[data-platform-testing-target='messageArea']")
    if (!messageArea) return

    messageArea.textContent = text
    messageArea.className = `text-sm p-3 rounded-md ${
      type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    }`
    messageArea.classList.remove('hidden')

    setTimeout(() => {
      messageArea.classList.add('hidden')
    }, 5000)
  }

  updatePhoneNumber(event) {
    const phoneInput = event.currentTarget
    const phoneNumber = phoneInput.value.trim()

    // Enable/disable WhatsApp button and template buttons based on phone input
    const waButton = this.waButtonTarget
    const templateButtons = this.templateButtonTargets
    const testStatusButtons = this.testStatusButtonTargets

    const hasPhone = phoneNumber.length > 0
    waButton.disabled = !hasPhone
    templateButtons.forEach((button) => {
      button.disabled = !hasPhone
    })

    // Enable test status buttons that are currently disabled (non-tested ones)
    testStatusButtons.forEach((button) => {
      if (button.dataset.tested === 'false') {
        button.disabled = !hasPhone
      }
    })

    // Store phone number for later use
    this.currentPhoneNumber = phoneNumber
  }

  async openWhatsApp(event) {
    const phoneNumber = this.currentPhoneNumber
    if (!phoneNumber) {
      alert('Digite um número de telefone primeiro')
      return
    }

    try {
      const response = await fetch(
        `/real_estate_admin/${this.realEstateAdminIdValue}/onboarding/get_whatsapp_link?phone_number=${encodeURIComponent(phoneNumber)}`,
        {
          method: 'GET',
          headers: {
            'X-CSRF-Token': this.getCSRFToken(),
          },
        }
      )

      const data = await response.json()

      if (data.success) {
        // Open WhatsApp link in new tab
        window.open(data.wa_link, '_blank')
      } else {
        alert(data.error || 'Erro ao gerar link do WhatsApp')
      }
    } catch (error) {
      console.error('Error getting WhatsApp link:', error)
      alert('Erro ao gerar link do WhatsApp')
    }
  }

  async sendTemplate(event) {
    const button = event.currentTarget
    const templateId = button.dataset.templateId
    const templateName = button.dataset.templateName
    const phoneNumber = this.currentPhoneNumber

    if (!phoneNumber) {
      alert('Digite um número de telefone primeiro')
      return
    }

    if (!templateId) {
      alert('Template ID não encontrado')
      return
    }

    // Disable button during request
    button.disabled = true
    const originalText = button.textContent
    button.textContent = 'Enviando...'

    try {
      const response = await fetch(
        `/real_estate_admin/${this.realEstateAdminIdValue}/onboarding/send_template`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCSRFToken(),
          },
          body: JSON.stringify({
            phone_number: phoneNumber,
            template_id: templateId,
            template_name: templateName,
          }),
        }
      )

      const data = await response.json()

      // Find the message area for this specific template
      const messageArea = document.querySelector(
        `[data-platform-testing-target="templateMessage"][data-template-id="${templateId}"]`
      )

      if (data.success) {
        this.showTemplateMessage(messageArea, data.message, 'success')
      } else {
        this.showTemplateMessage(messageArea, data.error, 'error')
      }
    } catch (error) {
      console.error('Error sending template:', error)
      const messageArea = document.querySelector(
        `[data-platform-testing-target="templateMessage"][data-template-id="${templateId}"]`
      )
      this.showTemplateMessage(messageArea, 'Erro ao enviar template', 'error')
    } finally {
      // Re-enable button
      setTimeout(() => {
        button.disabled = !this.currentPhoneNumber
        button.textContent = originalText
      }, 2000)
    }
  }

  showTemplateMessage(messageArea, text, type) {
    if (!messageArea) return

    messageArea.textContent = text
    messageArea.className = `text-sm p-2 rounded-md ${
      type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    }`
    messageArea.classList.remove('hidden')

    setTimeout(() => {
      messageArea.classList.add('hidden')
    }, 5000)
  }

  async toggleTestStatus(event) {
    console.log('toggleTestStatus called')
    const button = event.currentTarget
    const templateId = button.dataset.templateId
    const currentTested = button.dataset.tested === 'true'

    console.log('Template ID:', templateId)
    console.log('Current tested:', currentTested)
    console.log('Button data:', button.dataset)

    if (!templateId) {
      alert('Template ID não encontrado')
      return
    }

    // Disable button during request
    button.disabled = true
    const originalText = button.textContent
    button.textContent = currentTested ? 'Desmarcando...' : 'Marcando...'

    try {
      const response = await fetch(
        `/real_estate_admin/${this.realEstateAdminIdValue}/onboarding/toggle_template_test_status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCSRFToken(),
          },
          body: JSON.stringify({
            template_id: templateId,
            tested: currentTested,
          }),
        }
      )

      const data = await response.json()

      if (data.success) {
        // Update button appearance based on new status
        const newTested = data.tested
        button.dataset.tested = newTested.toString()

        if (newTested) {
          button.className =
            'w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500'
          button.textContent = '✓ Testado'
          button.disabled = false
        } else {
          button.className =
            'w-full px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed'
          button.textContent = 'Marcar como Testado'
          button.disabled = !this.currentPhoneNumber // Re-enable based on phone number
        }

        // Show success message briefly
        const originalButtonText = button.textContent
        button.textContent = data.message
        setTimeout(() => {
          button.textContent = originalButtonText
        }, 2000)
      } else {
        alert(data.error || 'Erro ao atualizar status')
        button.textContent = originalText
        button.disabled = false
      }
    } catch (error) {
      console.error('Error toggling test status:', error)
      alert('Erro ao atualizar status do teste')
      button.textContent = originalText
      button.disabled = false
    }
  }

  async analyzeCrmIds(event) {
    console.log('analyzeCrmIds called')
    const button = event.currentTarget
    const url = this.crmUrlInputTarget.value.trim()

    if (!url) {
      alert('Digite a URL de um imóvel primeiro')
      return
    }

    if (!this.isValidUrl(url)) {
      alert('Digite uma URL válida')
      return
    }

    // Get custom prompt if editor is visible and has content
    const customPrompt =
      this.hasPromptTextareaTarget &&
      !this.promptEditorTarget.classList.contains('hidden') &&
      this.promptTextareaTarget.value.trim()
        ? this.promptTextareaTarget.value.trim()
        : null

    // Show loading state
    this.analysisResultsTarget.classList.remove('hidden')
    this.analysisLoadingTarget.classList.remove('hidden')
    this.analysisContentTarget.innerHTML = ''

    // Disable button
    button.disabled = true
    const originalText = button.textContent
    button.textContent = 'Analisando...'

    try {
      const requestBody = { listing_url: url }
      if (customPrompt) {
        requestBody.custom_prompt = customPrompt
      }

      const response = await fetch(
        `/real_estate_admin/${this.realEstateAdminIdValue}/onboarding/analyze_crm_ids`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCSRFToken(),
          },
          body: JSON.stringify(requestBody),
        }
      )

      const data = await response.json()

      if (data.success) {
        this.renderAnalysisResults(data.analysis, data.tested_url)
      } else {
        this.showAnalysisError(data.error || 'Erro desconhecido na análise')
      }
    } catch (error) {
      console.error('Error analyzing CRM IDs:', error)
      this.showAnalysisError('Erro de conexão durante a análise')
    } finally {
      // Hide loading and re-enable button
      this.analysisLoadingTarget.classList.add('hidden')
      button.disabled = false
      button.textContent = originalText
    }
  }

  isValidUrl(string) {
    try {
      new URL(string)
      return true
    } catch (_) {
      return false
    }
  }

  renderAnalysisResults(analysis, testedUrl = null) {
    console.log('Rendering analysis results:', analysis)

    let html = ''

    // Summary
    if (analysis.summary) {
      const severity = analysis.summary.issues_severity || 'unknown'
      const severityColor =
        severity === 'high'
          ? 'bg-red-100 text-red-800'
          : severity === 'medium'
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-green-100 text-green-800'

      html += `
        <div class="mb-6 p-4 bg-blue-50 rounded-lg">
          <h4 class="font-semibold text-blue-900 mb-2">📊 Resumo da Análise</h4>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span class="font-medium">Total de Listings:</span>
              <span class="text-blue-700">${analysis.summary.total_listings || 0}</span>
            </div>
            <div>
              <span class="font-medium">Amostra Analisada:</span>
              <span class="text-blue-700">${analysis.summary.sample_size || 0}</span>
            </div>
            <div>
              <span class="font-medium">Padrões Encontrados:</span>
              <span class="text-blue-700">${(analysis.summary.patterns_found || []).join(', ')}</span>
            </div>
            <div>
              <span class="font-medium">Severidade:</span>
              <span class="inline-flex px-2 py-1 rounded-full text-xs ${severityColor}">${severity}</span>
            </div>
          </div>
        </div>
        ${
          testedUrl
            ? `
          <div class="mt-3 pt-3 border-t">
            <span class="font-medium text-blue-900">URL Testada:</span>
            <div class="mt-1">
              <a href="${testedUrl}" target="_blank" class="text-blue-600 hover:text-blue-800 underline break-all inline-flex items-center gap-1">
                ${testedUrl} <span class="text-xs">↗</span>
              </a>
            </div>
          </div>
        `
            : ''
        }
      `
    }

    // Patterns
    if (analysis.patterns && analysis.patterns.length > 0) {
      html += `
        <div class="mb-6">
          <h4 class="font-semibold text-gray-900 mb-3">🔍 Padrões de IDs Identificados</h4>
          <div class="space-y-3">
      `

      analysis.patterns.forEach((pattern) => {
        html += `
          <div class="border rounded-lg p-3">
            <div class="flex justify-between items-center mb-2">
              <span class="font-medium">${pattern.type || 'Desconhecido'}</span>
              <span class="text-sm text-gray-600">${pattern.count || 0} IDs (${pattern.percentage || 0}%)</span>
            </div>
            <div class="text-sm text-gray-600">
              <strong>Exemplos:</strong> ${(pattern.examples || []).join(', ')}
            </div>
          </div>
        `
      })

      html += '</div></div>'
    }

    // Web Check
    if (analysis.web_check) {
      const webCheck = analysis.web_check
      html += `
        <div class="mb-6 p-4 border rounded-lg">
          <h4 class="font-semibold text-gray-900 mb-3">🌐 Verificação no Site</h4>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span>URL Acessível:</span>
              <span class="${webCheck.url_accessible ? 'text-green-600' : 'text-red-600'}">
                ${webCheck.url_accessible ? '✅ Sim' : '❌ Não'}
              </span>
            </div>
            <div class="flex justify-between">
              <span>ID Encontrado na Página:</span>
              <span class="font-mono">${webCheck.id_found || 'Não encontrado'}</span>
            </div>
            <div class="flex justify-between">
              <span>ID Existe na Base:</span>
              <span class="${webCheck.id_exists_in_db ? 'text-green-600' : 'text-red-600'}">
                ${webCheck.id_exists_in_db ? '✅ Sim' : '❌ Não'}
              </span>
            </div>
            <div class="flex justify-between">
              <span>Padrão Compatível:</span>
              <span class="${webCheck.pattern_matches ? 'text-green-600' : 'text-red-600'}">
                ${webCheck.pattern_matches ? '✅ Sim' : '❌ Não'}
              </span>
            </div>
          </div>
        </div>
      `
    }

    // Issues
    if (analysis.issues && analysis.issues.length > 0) {
      html += `
        <div class="mb-6">
          <h4 class="font-semibold text-gray-900 mb-3">⚠️ Problemas Identificados</h4>
          <div class="space-y-3">
      `

      analysis.issues.forEach((issue) => {
        const severityColor =
          issue.severity === 'high'
            ? 'border-red-500 bg-red-50'
            : issue.severity === 'medium'
              ? 'border-yellow-500 bg-yellow-50'
              : 'border-blue-500 bg-blue-50'

        html += `
          <div class="border-l-4 ${severityColor} p-4 rounded">
            <div class="flex justify-between items-start mb-2">
              <h5 class="font-medium">${issue.type || 'Problema'}</h5>
              <span class="text-xs px-2 py-1 rounded bg-gray-200">${issue.severity || 'unknown'}</span>
            </div>
            <p class="text-sm text-gray-700 mb-2">${issue.description || ''}</p>
            ${issue.examples ? `<p class="text-xs text-gray-600"><strong>Exemplos:</strong> ${issue.examples.join(', ')}</p>` : ''}
            ${issue.impact ? `<p class="text-xs text-gray-600 mt-1"><strong>Impacto:</strong> ${issue.impact}</p>` : ''}
          </div>
        `
      })

      html += '</div></div>'
    }

    // Recommendations
    if (analysis.recommendations && analysis.recommendations.length > 0) {
      html += `
        <div class="mb-6">
          <h4 class="font-semibold text-gray-900 mb-3">💡 Recomendações</h4>
          <div class="space-y-3">
      `

      analysis.recommendations.forEach((rec, index) => {
        const priorityColor =
          rec.priority === 'high'
            ? 'text-red-600'
            : rec.priority === 'medium'
              ? 'text-yellow-600'
              : 'text-green-600'

        html += `
          <div class="border rounded-lg p-3">
            <div class="flex justify-between items-start mb-2">
              <span class="font-medium">${index + 1}. ${rec.action || ''}</span>
              <span class="text-xs ${priorityColor}">${rec.priority || 'low'} priority</span>
            </div>
            <p class="text-sm text-gray-700">${rec.reason || ''}</p>
            ${rec.effort ? `<p class="text-xs text-gray-500 mt-1">Esforço estimado: ${rec.effort}</p>` : ''}
          </div>
        `
      })

      html += '</div></div>'
    }

    // Error handling
    if (analysis.error) {
      html += `
        <div class="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 class="font-semibold text-red-900 mb-2">❌ Erro na Análise</h4>
          <p class="text-sm text-red-700">${analysis.message || 'Erro desconhecido'}</p>
        </div>
      `
    }

    this.analysisContentTarget.innerHTML = html
  }

  showAnalysisError(message) {
    this.analysisContentTarget.innerHTML = `
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <h4 class="font-semibold text-red-900 mb-2">❌ Erro na Análise</h4>
        <p class="text-sm text-red-700">${message}</p>
      </div>
    `
  }

  togglePromptEditor(event) {
    console.log('togglePromptEditor called')

    if (this.promptEditorTarget.classList.contains('hidden')) {
      // Show editor and load default prompt if empty
      this.promptEditorTarget.classList.remove('hidden')
      this.editPromptButtonTarget.textContent = '📝 Ocultar Prompt'

      if (!this.promptTextareaTarget.value.trim()) {
        this.loadDefaultPrompt()
      }
    } else {
      // Hide editor
      this.promptEditorTarget.classList.add('hidden')
      this.editPromptButtonTarget.textContent = '📝 Editar Prompt'
    }
  }

  async loadDefaultPrompt(event = null) {
    console.log('loadDefaultPrompt called')

    try {
      this.promptTextareaTarget.placeholder = 'Carregando prompt padrão...'
      this.promptTextareaTarget.disabled = true

      const response = await fetch(
        `/real_estate_admin/${this.realEstateAdminIdValue}/onboarding/get_default_prompt`,
        {
          method: 'GET',
          headers: {
            'X-CSRF-Token': this.getCSRFToken(),
          },
        }
      )

      const data = await response.json()

      if (data.success) {
        this.promptTextareaTarget.value = data.prompt
        this.promptTextareaTarget.placeholder = ''

        if (event) {
          // Show brief success message
          const originalText = event.currentTarget.textContent
          event.currentTarget.textContent = '✅ Restaurado'
          setTimeout(() => {
            event.currentTarget.textContent = originalText
          }, 2000)
        }
      } else {
        alert('Erro ao carregar prompt padrão')
      }
    } catch (error) {
      console.error('Error loading default prompt:', error)
      alert('Erro ao carregar prompt padrão')
    } finally {
      this.promptTextareaTarget.disabled = false
    }
  }

  loadLeadTemplate(event) {
    const template = event.currentTarget.value

    // Define predefined templates
    const templates = {
      apartment_sale: {
        name: 'João Silva Teste',
        phone: '+5511999999999',
        email: 'joao.teste@example.com',
        budget: '300000',
        bedrooms: '2',
        transaction_type: 'sale',
        message:
          'Olá, tenho interesse em apartamentos de 2 quartos para comprar na região de São Paulo. Meu orçamento é de até R$300.000.',
      },
      house_rent: {
        name: 'Maria Santos Teste',
        phone: '+5511888888888',
        email: 'maria.teste@example.com',
        budget: '2500',
        bedrooms: '3',
        transaction_type: 'rent',
        message:
          'Procuro casa de 3 quartos para alugar em Campinas. Tenho orçamento de até R$2.500 por mês.',
      },
      commercial_sale: {
        name: 'Carlos Empresa Teste',
        phone: '+5511777777777',
        email: 'carlos.teste@example.com',
        budget: '500000',
        bedrooms: '0',
        transaction_type: 'sale',
        message: 'Preciso de um escritório comercial para comprar. Orçamento de até R$500.000.',
      },
    }

    if (templates[template]) {
      const data = templates[template]
      this.leadNameTarget.value = data.name
      this.leadPhoneTarget.value = data.phone
      this.leadEmailTarget.value = data.email
      if (this.hasLeadBudgetTarget) this.leadBudgetTarget.value = data.budget
      if (this.hasLeadBedroomsTarget) this.leadBedroomsTarget.value = data.bedrooms
      this.leadTransactionTypeTarget.value = data.transaction_type
      this.leadMessageTarget.value = data.message
    }
  }

  async createTestLead(event) {
    console.log('createTestLead called')
    const button = event.currentTarget

    // Get form data (only fields that exist in Lead model)
    const leadData = {
      name: this.leadNameTarget.value.trim(),
      phone: this.leadPhoneTarget.value.trim(),
      email: this.leadEmailTarget.value.trim(),
      transaction_type: this.leadTransactionTypeTarget.value,
      message: this.leadMessageTarget.value.trim(),
      listing_id: this.leadListingTarget.value.trim() || null,
      origin_type: this.leadOriginTarget.value,
      custom_origin_domain: this.hasCustomOriginDomainTarget
        ? this.customOriginDomainTarget.value.trim()
        : null,
    }

    // Validate required fields
    if (!leadData.name || !leadData.phone) {
      alert('Nome e telefone são obrigatórios')
      return
    }

    // Show loading state
    this.leadTestResultsTarget.classList.remove('hidden')
    this.leadTestLoadingTarget.classList.remove('hidden')
    this.leadTestContentTarget.innerHTML = ''

    // Disable button
    button.disabled = true
    const originalText = button.textContent
    button.textContent = 'Criando Lead...'

    try {
      const response = await fetch(
        `/real_estate_admin/${this.realEstateAdminIdValue}/onboarding/create_test_lead`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCSRFToken(),
          },
          body: JSON.stringify(leadData),
        }
      )

      const data = await response.json()

      if (data.success) {
        this.showLeadTestSuccess(data)
      } else {
        this.showLeadTestError(data.error || 'Erro desconhecido ao criar lead')
      }
    } catch (error) {
      console.error('Error creating test lead:', error)
      this.showLeadTestError('Erro de conexão ao criar lead de teste')
    } finally {
      // Hide loading and re-enable button
      this.leadTestLoadingTarget.classList.add('hidden')
      button.disabled = false
      button.textContent = originalText
    }
  }

  showLeadTestSuccess(data) {
    const html = `
      <div class="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 class="font-semibold text-green-900 mb-2">✅ Lead Criado com Sucesso!</h4>
        <div class="text-sm text-green-700 space-y-1">
          <p><strong>Mensagem:</strong> ${data.message}</p>
          <p><strong>Lead ID:</strong> ${data.lead_id}</p>
          ${data.external_id ? `<p><strong>External ID:</strong> ${data.external_id}</p>` : ''}
          <p><strong>Imóvel:</strong> ${data.listing_info}</p>
          <p><strong>Origem:</strong> ${data.origin_info}</p>
          <p><strong>CRMs Configurados:</strong> ${data.crm_count}</p>
          ${
            data.crm_names && data.crm_names.length > 0
              ? `<p><strong>Destinos:</strong> ${data.crm_names.join(', ')}</p>`
              : ''
          }
        </div>
        
        ${data.crm_results && data.crm_results.length > 0 ? this.renderCrmResults(data.crm_results) : ''}
        
        <div class="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
          <p class="text-sm text-blue-700">
            <strong>💡 Próximos Passos:</strong> O lead foi automaticamente enviado para todos os CRMs configurados. 
            Verifique os sistemas de destino para confirmar o recebimento e como as informações de origem foram processadas.
          </p>
        </div>
      </div>
    `
    this.leadTestContentTarget.innerHTML = html
  }

  renderCrmResults(crmResults) {
    if (!crmResults || crmResults.length === 0) return ''

    const resultsHtml = crmResults
      .map((result) => {
        const statusColor = this.getStatusColor(result.status)
        const statusIcon = this.getStatusIcon(result.status)

        return `
        <div class="border rounded p-2 ${statusColor.bg}">
          <div class="flex justify-between items-center">
            <span class="font-medium text-sm">${statusIcon} ${result.crm_name}</span>
            <span class="text-xs ${statusColor.text}">${result.created_at}</span>
          </div>
          <div class="text-xs ${statusColor.text} mt-1">
            ${result.response_summary}
          </div>
          ${
            result.external_id
              ? `<div class="text-xs text-gray-600 mt-1">ID: ${result.external_id}</div>`
              : ''
          }
        </div>
      `
      })
      .join('')

    return `
      <div class="mt-4 border-t pt-3">
        <h5 class="font-semibold text-green-900 mb-2">📊 Resultados dos CRMs:</h5>
        <div class="space-y-2">
          ${resultsHtml}
        </div>
      </div>
    `
  }

  getStatusColor(status) {
    switch (status) {
      case 'success':
        return { bg: 'bg-green-50 border-green-200', text: 'text-green-700' }
      case 'error':
        return { bg: 'bg-red-50 border-red-200', text: 'text-red-700' }
      case 'processing':
        return { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700' }
      case 'pending':
        return { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-600' }
      default:
        return { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-600' }
    }
  }

  getStatusIcon(status) {
    switch (status) {
      case 'success':
        return '✅'
      case 'error':
        return '❌'
      case 'processing':
        return '⏳'
      case 'pending':
        return '⏸️'
      default:
        return '❓'
    }
  }

  showLeadTestError(message) {
    const html = `
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <h4 class="font-semibold text-red-900 mb-2">❌ Erro ao Criar Lead</h4>
        <p class="text-sm text-red-700">${message}</p>
        <div class="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p class="text-sm text-yellow-700">
            <strong>💡 Dica:</strong> Verifique se existem CRMs configurados e habilitados para este admin.
          </p>
        </div>
      </div>
    `
    this.leadTestContentTarget.innerHTML = html
  }

  async validateListingsUrls(event) {
    console.log('validateListingsUrls called')
    const button = event.currentTarget

    // Show loading state
    this.urlValidationResultsTarget.classList.remove('hidden')
    this.urlValidationLoadingTarget.classList.remove('hidden')
    this.urlValidationContentTarget.innerHTML = ''

    // Disable button
    button.disabled = true
    const originalText = button.textContent
    button.textContent = 'Validando...'

    try {
      const response = await fetch(
        `/real_estate_admin/${this.realEstateAdminIdValue}/onboarding/validate_listings_urls`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCSRFToken(),
          },
        }
      )

      const data = await response.json()

      if (data.success) {
        this.renderUrlValidationResults(data.summary, data.results)
      } else {
        this.showUrlValidationError(data.error || 'Erro desconhecido na validação')
      }
    } catch (error) {
      console.error('Error validating listings URLs:', error)
      this.showUrlValidationError('Erro de conexão durante a validação')
    } finally {
      // Hide loading and re-enable button
      this.urlValidationLoadingTarget.classList.add('hidden')
      button.disabled = false
      button.textContent = originalText
    }
  }

  async validateCrawlerData(event) {
    console.log('validateCrawlerData called')
    const button = event.currentTarget

    // Show loading state
    this.crawlerValidationResultsTarget.classList.remove('hidden')
    this.crawlerValidationLoadingTarget.classList.remove('hidden')
    this.crawlerValidationContentTarget.innerHTML = ''

    // Disable button
    button.disabled = true
    const originalText = button.textContent
    button.textContent = 'Validando Crawler...'

    try {
      const response = await fetch(
        `/real_estate_admin/${this.realEstateAdminIdValue}/onboarding/validate_crawler_data`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCSRFToken(),
          },
        }
      )

      const data = await response.json()

      if (data.success) {
        this.renderCrawlerValidationResults(data.summary, data.results)
      } else {
        this.showCrawlerValidationError(data.error || 'Erro desconhecido na validação do crawler')
      }
    } catch (error) {
      console.error('Error validating crawler data:', error)
      this.showCrawlerValidationError('Erro de conexão durante a validação do crawler')
    } finally {
      // Hide loading and re-enable button
      this.crawlerValidationLoadingTarget.classList.add('hidden')
      button.disabled = false
      button.textContent = originalText
    }
  }

  async validateMediaImages(event) {
    console.log('validateMediaImages called')
    const button = event.currentTarget

    // Show loading state
    this.mediaValidationResultsTarget.classList.remove('hidden')
    this.mediaValidationLoadingTarget.classList.remove('hidden')
    this.mediaValidationContentTarget.innerHTML = ''

    // Disable button
    button.disabled = true
    const originalText = button.textContent
    button.textContent = 'Validando Imagens...'

    try {
      const response = await fetch(
        `/real_estate_admin/${this.realEstateAdminIdValue}/onboarding/validate_media_images`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCSRFToken(),
          },
        }
      )

      const data = await response.json()

      if (data.success) {
        this.renderMediaValidationResults(data.summary, data.results)
      } else {
        this.showMediaValidationError(data.error || 'Erro desconhecido na validação de imagens')
      }
    } catch (error) {
      console.error('Error validating media images:', error)
      this.showMediaValidationError('Erro de conexão durante a validação de imagens')
    } finally {
      // Hide loading and re-enable button
      this.mediaValidationLoadingTarget.classList.add('hidden')
      button.disabled = false
      button.textContent = originalText
    }
  }

  renderUrlValidationResults(summary, results) {
    console.log('Rendering URL validation results:', summary, results)

    let html = ''

    // Summary
    html += `
      <div class="mb-6 p-4 bg-green-50 rounded-lg">
        <h4 class="font-semibold text-green-900 mb-2">📊 Resumo da Validação</h4>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span class="font-medium">Total Verificado:</span>
            <span class="text-green-700">${summary.total_checked || 0}</span>
          </div>
          <div>
            <span class="font-medium">URLs Válidas:</span>
            <span class="text-green-700">${summary.valid_urls || 0}</span>
          </div>
          <div>
            <span class="font-medium">URLs Inválidas:</span>
            <span class="text-red-700">${summary.invalid_urls || 0}</span>
          </div>
          <div>
            <span class="font-medium">Erros:</span>
            <span class="text-gray-700">${summary.errors || 0}</span>
          </div>
        </div>
      </div>
    `

    // Results list
    if (results && results.length > 0) {
      html += `
        <div class="space-y-3">
          <h4 class="font-semibold text-gray-900">🔍 Detalhes por Imóvel</h4>
      `

      results.forEach((result, index) => {
        const statusIcon =
          result.status === 'valid' ? '✅' : result.status === 'invalid' ? '❌' : '⚠️'
        const statusColor =
          result.status === 'valid'
            ? 'border-green-500 bg-green-50'
            : result.status === 'invalid'
              ? 'border-red-500 bg-red-50'
              : 'border-yellow-500 bg-yellow-50'

        const confidenceColor =
          result.confidence === 'high'
            ? 'text-green-600'
            : result.confidence === 'medium'
              ? 'text-yellow-600'
              : 'text-red-600'

        // Method indicator
        const methodBadge =
          '<span class="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800">🤖 GPT com Reasoning</span>'

        html += `
          <div class="border-l-4 ${statusColor} p-4 rounded">
            <div class="flex justify-between items-start mb-2">
              <h5 class="font-medium">${statusIcon} Imóvel ${index + 1} - CRM ID: ${result.crm_id || 'N/A'}</h5>
              <div class="flex gap-2">
                ${methodBadge}
                <span class="text-xs px-2 py-1 rounded bg-gray-200">${result.status}</span>
              </div>
            </div>
            <p class="text-sm text-gray-600 mb-2">
              ${result.url ? `<a href="${result.url}" target="_blank" class="text-blue-600 hover:text-blue-800 underline break-all inline-flex items-center gap-1">${result.url} <span class="text-xs">↗</span></a>` : 'URL não disponível'}
            </p>
            ${result.confidence ? `<p class="text-xs ${confidenceColor} mb-2">Confiança: ${result.confidence}</p>` : ''}
            
            ${this.renderDetailedComparison(result)}
            
            ${
              result.issues && result.issues.length > 0
                ? `<div class="text-xs text-gray-600">
                <strong>Problemas:</strong>
                <ul class="list-disc list-inside mt-1">
                  ${result.issues.map((issue) => `<li>${issue}</li>`).join('')}
                </ul>
              </div>`
                : ''
            }
            
            ${
              result.crawler_error
                ? `<div class="text-xs text-red-600 mt-2">
                <strong>Erro do Crawler:</strong> ${result.crawler_error}
              </div>`
                : ''
            }
          </div>
        `
      })

      html += '</div>'
    }

    this.urlValidationContentTarget.innerHTML = html
  }

  showUrlValidationError(message) {
    this.urlValidationContentTarget.innerHTML = `
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <h4 class="font-semibold text-red-900 mb-2">❌ Erro na Validação</h4>
        <p class="text-sm text-red-700">${message}</p>
      </div>
    `
  }

  renderDetailedComparison(result) {
    if (!result.gpt_analysis) {
      return ''
    }

    let html = `
      <div class="text-xs bg-blue-50 rounded p-3 mb-2">
        <div class="flex justify-between items-center mb-2">
          <strong class="text-blue-900">🤖 Resposta Completa do GPT</strong>
          <span class="text-blue-700">${result.confidence}</span>
        </div>
        
        <div class="bg-gray-100 rounded p-2 mb-2">
          <pre class="text-xs text-gray-800 whitespace-pre-wrap">${JSON.stringify(result.gpt_analysis, null, 2)}</pre>
        </div>
    `

    // Show bank data comparison
    if (result.bank_data) {
      html += `
        <div class="border-t pt-2">
          <strong class="text-blue-900">📋 Dados do Banco:</strong>
          <div class="text-xs text-gray-600 mt-1">
            <div>CRM ID: ${result.bank_data.crm_id}</div>
            <div>Título: ${result.bank_data.title_raw || 'N/A'}</div>
            <div>Tipo: ${result.bank_data.property_type || 'N/A'}</div>
            <div>Quartos: ${result.bank_data.bedrooms || 'N/A'} | Banheiros: ${result.bank_data.bathrooms || 'N/A'}</div>
          </div>
        </div>
      `
    }

    html += '</div>'
    return html
  }

  renderCrawlerValidationResults(summary, results) {
    console.log('Rendering crawler validation results:', summary, results)

    let html = ''

    // Summary
    html += `
      <div class="mb-6 p-4 bg-blue-50 rounded-lg">
        <h4 class="font-semibold text-blue-900 mb-2">📊 Resumo da Validação do Crawler</h4>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span class="font-medium">Total Verificado:</span>
            <span class="text-blue-700">${summary.total_checked || 0}</span>
          </div>
          <div>
            <span class="font-medium">Extrações Bem-Sucedidas:</span>
            <span class="text-green-700">${summary.successful_extractions || 0}</span>
          </div>
          <div>
            <span class="font-medium">Erros:</span>
            <span class="text-red-700">${summary.errors || 0}</span>
          </div>
        </div>
      </div>
    `

    // Results list
    if (results && results.length > 0) {
      html += `
        <div class="space-y-4">
          <h4 class="font-semibold text-gray-900">🔍 Comparação Dados Banco vs Crawler</h4>
      `

      results.forEach((result, index) => {
        const statusIcon = result.status === 'success' ? '✅' : '❌'
        const statusColor =
          result.status === 'success' ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'

        html += `
          <div class="border-l-4 ${statusColor} p-4 rounded">
            <div class="flex justify-between items-start mb-3">
              <h5 class="font-medium">${statusIcon} Imóvel ${index + 1} - CRM ID: ${result.crm_id || 'N/A'}</h5>
              <span class="text-xs px-2 py-1 rounded bg-gray-200">${result.status}</span>
            </div>
            
            ${
              result.error_message
                ? `<div class="text-xs text-red-600 mb-3">
                <strong>Erro:</strong> ${result.error_message}
              </div>`
                : ''
            }
            
            <!-- Side by side comparison -->
            <div class="grid grid-cols-2 gap-4 text-sm">
              <!-- Banco de Dados -->
              <div class="bg-gray-50 p-3 rounded">
                <h6 class="font-semibold text-gray-800 mb-2">📂 Dados do Banco</h6>
                <div class="space-y-1">
                  <div><strong>Título:</strong> ${result.bank_data?.title_raw || 'N/A'}</div>
                  <div><strong>Descrição:</strong> ${result.bank_data?.description_raw || 'N/A'}</div>
                  <div><strong>Quartos:</strong> ${result.bank_data?.bedrooms || 'N/A'}</div>
                  <div><strong>Banheiros:</strong> ${result.bank_data?.bathrooms || 'N/A'}</div>
                  <div><strong>Preço Venda:</strong> ${result.bank_data?.list_price ? `R$ ${result.bank_data.list_price.toLocaleString()}` : 'N/A'}</div>
                  <div><strong>Preço Aluguel:</strong> ${result.bank_data?.rental_price ? `R$ ${result.bank_data.rental_price.toLocaleString()}` : 'N/A'}</div>
                </div>
              </div>
              
              <!-- Crawler -->
              <div class="bg-blue-50 p-3 rounded">
                <h6 class="font-semibold text-blue-800 mb-2">🔧 Dados do Crawler</h6>
                ${
                  result.crawler_data
                    ? `
                  <div class="space-y-1">
                    <div><strong>Título:</strong> ${result.crawler_data?.title_raw || 'N/A'}</div>
                    <div><strong>Descrição:</strong> ${result.crawler_data?.description_raw || 'N/A'}</div>
                    <div><strong>Quartos:</strong> ${result.crawler_data?.bedrooms || 'N/A'}</div>
                    <div><strong>Banheiros:</strong> ${result.crawler_data?.bathrooms || 'N/A'}</div>
                    <div><strong>Preço Venda:</strong> ${result.crawler_data?.list_price ? `R$ ${result.crawler_data.list_price.toLocaleString()}` : 'N/A'}</div>
                    <div><strong>Preço Aluguel:</strong> ${result.crawler_data?.rental_price ? `R$ ${result.crawler_data.rental_price.toLocaleString()}` : 'N/A'}</div>
                  </div>
                `
                    : '<div class="text-red-600">Não foi possível extrair dados</div>'
                }
              </div>
            </div>
          </div>
        `
      })

      html += '</div>'
    }

    this.crawlerValidationContentTarget.innerHTML = html
  }

  showCrawlerValidationError(message) {
    this.crawlerValidationContentTarget.innerHTML = `
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <h4 class="font-semibold text-red-900 mb-2">❌ Erro na Validação do Crawler</h4>
        <p class="text-sm text-red-700">${message}</p>
      </div>
    `
  }

  renderMediaValidationResults(summary, results) {
    console.log('Rendering media validation results:', summary, results)

    let html = ''

    // Summary
    html += `
      <div class="mb-6 p-4 bg-purple-50 rounded-lg">
        <h4 class="font-semibold text-purple-900 mb-2">📊 Resumo da Validação de Imagens</h4>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span class="font-medium">Total Verificado:</span>
            <span class="text-purple-700">${summary.total_checked || 0}</span>
          </div>
          <div>
            <span class="font-medium">Imagens Válidas:</span>
            <span class="text-green-700">${summary.valid_images || 0}</span>
          </div>
          <div>
            <span class="font-medium">Imagens Inválidas:</span>
            <span class="text-red-700">${summary.invalid_images || 0}</span>
          </div>
          <div>
            <span class="font-medium">Erros:</span>
            <span class="text-gray-700">${summary.errors || 0}</span>
          </div>
        </div>
      </div>
    `

    // Results list
    if (results && results.length > 0) {
      html += `
        <div class="space-y-3">
          <h4 class="font-semibold text-gray-900">🖼️ Detalhes por Imagem</h4>
      `

      results.forEach((result, index) => {
        const statusIcon =
          result.status === 'valid' ? '✅' : result.status === 'invalid' ? '❌' : '⚠️'
        const statusColor =
          result.status === 'valid'
            ? 'border-green-500 bg-green-50'
            : result.status === 'invalid'
              ? 'border-red-500 bg-red-50'
              : 'border-yellow-500 bg-yellow-50'

        html += `
          <div class="border-l-4 ${statusColor} p-4 rounded">
            <div class="flex justify-between items-start mb-3">
              <div>
                <h5 class="font-medium">${statusIcon} Imóvel ${index + 1}</h5>
                <div class="text-sm text-gray-600 mt-1">
                  <span class="font-medium">CRM ID:</span> <span class="font-mono bg-gray-100 px-2 py-1 rounded text-xs">${result.crm_id || 'N/A'}</span>
                </div>
                <div class="text-sm text-gray-600 mt-1">
                  <span class="font-medium">Título:</span> ${result.title_raw || 'Sem título'}
                </div>
              </div>
              <span class="text-xs px-2 py-1 rounded bg-gray-200">${result.status}</span>
            </div>
            
            <!-- Image URL -->
            ${
              result.image_url
                ? `<div class="mb-3">
                <span class="text-sm font-medium">URL da Imagem:</span>
                <div class="mt-1">
                  <a href="${result.image_url}" target="_blank" class="text-blue-600 hover:text-blue-800 underline break-all inline-flex items-center gap-1 text-xs">
                    ${result.image_url} <span class="text-xs">↗</span>
                  </a>
                </div>
              </div>`
                : ''
            }
            
            <!-- Validation Details -->
            ${
              result.validation_details
                ? `
              <div class="bg-gray-50 p-3 rounded mb-3">
                <h6 class="font-semibold text-gray-800 mb-2">📋 Detalhes da Validação</h6>
                <div class="text-xs space-y-1">
                  <div><strong>MIME Type HTTP:</strong> ${result.validation_details.http_mime_type || 'N/A'}</div>
                  <div><strong>Tipo Detectado:</strong> ${result.validation_details.detected_type || 'N/A'}</div>
                  <div><strong>MIME Type:</strong> ${result.validation_details.mime_type || 'N/A'}</div>
                  <div><strong>Nome do Arquivo:</strong> ${result.validation_details.filename || 'N/A'}</div>
                </div>
              </div>
            `
                : ''
            }
            
            <!-- Error Message -->
            ${
              result.error_message
                ? `<div class="text-xs text-red-600 mb-2">
                <strong>Erro:</strong> ${result.error_message}
              </div>`
                : ''
            }
            
            <!-- Format Status -->
            <div class="text-xs">
              ${
                result.status === 'valid'
                  ? '<span class="text-green-600 font-medium">✅ Formato aceito pela Meta</span>'
                  : result.status === 'invalid'
                    ? '<span class="text-red-600 font-medium">❌ Formato não aceito pela Meta (use JPEG ou PNG)</span>'
                    : '<span class="text-yellow-600 font-medium">⚠️ Erro ao verificar formato</span>'
              }
            </div>
          </div>
        `
      })

      html += '</div>'
    }

    this.mediaValidationContentTarget.innerHTML = html
  }

  showMediaValidationError(message) {
    this.mediaValidationContentTarget.innerHTML = `
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <h4 class="font-semibold text-red-900 mb-2">❌ Erro na Validação de Imagens</h4>
        <p class="text-sm text-red-700">${message}</p>
      </div>
    `
  }

  getCSRFToken() {
    const token = document.querySelector('[name="csrf-token"]')
    return token ? token.content : ''
  }
}
